import dayjs from 'dayjs';
import React, { useMemo } from 'react';

import { ResourceTypeDto, TaskItemDto, TaskStatusDto } from '@/api/generated';
import { CustomTableFilterDefinition } from '@/components/table/components/formBuilder/model/CustomTableFilterDefinition.model.ts';
import { CustomTableFilterTypeEnum } from '@/components/table/components/formBuilder/model/CustomTableFilterTypeEnum.ts';
import {
  CustomTable,
  CustomTableColumn,
  CustomTableVariant,
} from '@/components/table/CustomTable.comp.tsx';
import { CustomTableQuickFilter } from '@/components/table/quickFilters/CustomTableQuickFilter.model.ts';
import {
  daysAgo,
  hasOption,
  isEmptyDate,
  isOption,
  isSameDay,
  startOfToday,
} from '@/components/table/quickFilters/quickFilter.util.ts';
import { TimelineMessagesTableParams } from '@/components/timelineMessages/comp/confirmMessagesReadButton/model/TimelineMessagesTableParams.model';
import { AnnouncementAnchorKey } from '@/contexts/announcements/announcements.definitions.ts';
import { useDictionariesContext } from '@/contexts/dictionaries/Dictionaries.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { TableBasicProps } from '@/models/TableBasicProps.model.ts';
import { useGetSelectOptions } from '@/models/useGetSelectOptions.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { TasksListTableRow } from '@/views/tasks/list/components/table/components/TasksListTableRow.comp.tsx';
import {
  initialTasksListSearchFormModel,
  TasksListSearchFormModel,
} from '@/views/tasks/list/model/TasksListSearchForm.model.ts';
import {
  initialTasksListTableState,
  TasksListTableState,
} from '@/views/tasks/list/model/TasksListTableState.model.ts';
import { useGetTasks } from '@/views/tasks/list/utils/useGetTasks.util.ts';
import { useTasksFormValidation } from '@/views/tasks/list/utils/useTasksForm.validation.ts';

type Props = {
  initialParams?: Partial<TasksListSearchFormModel>;
  size?: CustomTableVariant;
  typeDefined?: boolean;
  withActions?: boolean;
} & TableBasicProps<TaskItemDto, TasksListSearchFormModel, TimelineMessagesTableParams>;

const initialTaskStatusesListTaskStatus: Set<TaskStatusDto> = new Set([
  TaskStatusDto.DoneNotConfirmed,
  TaskStatusDto.InProgress,
  TaskStatusDto.New,
  TaskStatusDto.Postponed,
]);

export const TasksListTable: React.FC<Props> = props => {
  const {
    additionalButtons,
    customRow,
    initialParams,
    onCustomAction,
    request,
    size,
    tableKey,
    typeDefined,
    withActions,
  } = props;
  const { t } = useTranslationWithPrefix('views.tasks.list.table');
  const { getTasks, isLoading } = useGetTasks();
  const validation = useTasksFormValidation();
  const { USERS_SHORT } = useDictionariesContext();
  const { userSelectOption } = useUserContext();
  const { resourceTypeOptions, taskStatusOptions } = useGetSelectOptions();
  const initialTaskStatuses = taskStatusOptions.filter(it =>
    initialTaskStatusesListTaskStatus.has(it.value),
  );

  const filters: CustomTableFilterDefinition<TasksListSearchFormModel>[] = useMemo(() => {
    const filters: CustomTableFilterDefinition<TasksListSearchFormModel>[] = [
      {
        label: t('head.creatorUserId'),
        name: 'creatorUserId',
        options: USERS_SHORT,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
      {
        label: t('head.assignedUserId'),
        name: 'assignedUserId',
        options: USERS_SHORT,
        type: CustomTableFilterTypeEnum.Select,
        xs: 3,
      },
      {
        announcementAnchorKey: AnnouncementAnchorKey.TASKS_FULL_TEXT_SEARCH,
        label: t('head.fullTextSearchInput'),
        name: 'fullTextSearchInput',
        placeholder: t('head.fullTextSearchInput'),
        type: CustomTableFilterTypeEnum.Text,
        xs: 4,
      },
      {
        label: t('head.dueDateFrom'),
        name: 'dueDateFrom',
        type: CustomTableFilterTypeEnum.Date,
        xs: 2,
      },
      {
        label: t('head.dueDateTo'),
        name: 'dueDateTo',
        type: CustomTableFilterTypeEnum.Date,
        xs: 2,
      },
      {
        label: t('head.creationDateFrom'),
        name: 'creationDateFrom',
        type: CustomTableFilterTypeEnum.Date,
        xs: 2,
      },
      {
        label: t('head.creationDateTo'),
        name: 'creationDateTo',
        type: CustomTableFilterTypeEnum.Date,
        xs: 2,
      },
      {
        isMulti: true,
        label: t('head.taskStatuses'),
        name: 'taskStatuses',
        options: taskStatusOptions,
        type: CustomTableFilterTypeEnum.Select,
        xs: 4,
      },
    ];
    if (typeDefined !== true) {
      filters.splice(3, 0, {
        isMulti: true,
        label: t('head.contextResourceTypes'),
        name: 'contextResourceTypes',
        options: resourceTypeOptions.filter(
          option =>
            option.value !== ResourceTypeDto.SettlementPayment &&
            option.value !== ResourceTypeDto.CarLogEntry,
        ),
        type: CustomTableFilterTypeEnum.Select,
        xs: 2,
      });
    }
    return filters;
  }, [USERS_SHORT, resourceTypeOptions, taskStatusOptions, t, typeDefined]);

  const quickFilters: CustomTableQuickFilter<TasksListSearchFormModel>[] = useMemo(() => {
    const endOfWeek = () => dayjs().endOf('week').startOf('day');
    const dueBetween = (from: () => dayjs.Dayjs | null, to: () => dayjs.Dayjs) => ({
      apply: () => ({ dueDateFrom: from(), dueDateTo: to() }),
      clear: () => ({ dueDateFrom: null, dueDateTo: null }),
      group: 'due',
      isActive: (params: TasksListSearchFormModel) => {
        const fromDay = from();
        return (
          (fromDay ? isSameDay(params.dueDateFrom, fromDay) : isEmptyDate(params.dueDateFrom)) &&
          isSameDay(params.dueDateTo, to())
        );
      },
    });

    return [
      {
        apply: () => ({
          assignedUserId: userSelectOption,
          creatorUserId: null,
          watchersUserIds: [],
        }),
        clear: () => ({ assignedUserId: null }),
        group: 'who',
        id: 'mine',
        isActive: params => isOption(params.assignedUserId, userSelectOption.value),
        label: t('quickFilters.mine'),
      },
      {
        apply: () => ({
          assignedUserId: null,
          creatorUserId: userSelectOption,
          watchersUserIds: [],
        }),
        clear: () => ({ creatorUserId: null }),
        group: 'who',
        id: 'createdByMe',
        isActive: params => isOption(params.creatorUserId, userSelectOption.value),
        label: t('quickFilters.createdByMe'),
      },
      {
        apply: () => ({
          assignedUserId: null,
          creatorUserId: null,
          watchersUserIds: [userSelectOption],
        }),
        clear: () => ({ watchersUserIds: [] }),
        group: 'who',
        id: 'watched',
        isActive: params => hasOption(params.watchersUserIds, userSelectOption.value),
        label: t('quickFilters.watched'),
      },
      {
        ...dueBetween(
          () => null,
          () => daysAgo(1),
        ),
        id: 'overdue',
        label: t('quickFilters.overdue'),
      },
      {
        ...dueBetween(startOfToday, startOfToday),
        id: 'dueToday',
        label: t('quickFilters.dueToday'),
      },
      {
        ...dueBetween(startOfToday, endOfWeek),
        id: 'dueThisWeek',
        label: t('quickFilters.dueThisWeek'),
      },
      {
        apply: () => ({ creationDateFrom: startOfToday(), creationDateTo: null }),
        clear: () => ({ creationDateFrom: null }),
        id: 'createdToday',
        isActive: params =>
          isSameDay(params.creationDateFrom, startOfToday()) && isEmptyDate(params.creationDateTo),
        label: t('quickFilters.createdToday'),
      },
    ];
  }, [t, userSelectOption]);

  const tableHead: CustomTableColumn<string>[] = useMemo(() => {
    const head = [
      {
        id: 'title',
        label: t('body.title'),
        sx: { width: '30%' },
      },
      {
        id: 'creator',
        label: t('body.creator'),
        sx: { width: '9%' },
      },
      {
        id: 'assignedUser',
        label: t('body.assignedUser'),
        sx: { width: '10%' },
      },
      {
        id: 'dueDate',
        label: t('body.dueDate'),
        sx: { width: '13%' },
      },
      {
        id: 'contextResourceType',
        label: t('body.contextResourceType'),
        sx: { width: '14%' },
      },
      {
        id: 'status',
        label: t('body.status'),
        sx: { width: '14%' },
      },
    ];
    if (withActions) {
      head.push({
        id: 'actions',
        label: t('body.actions'),
        sx: { width: '10%' },
      });
    }
    return head;
  }, [t, withActions]);

  const req = request ?? getTasks;
  const init = async (initialState: TasksListTableState) => {
    return await req(initialState);
  };

  return (
    <CustomTable<string, TasksListSearchFormModel, TaskItemDto, TimelineMessagesTableParams>
      additionalButtons={additionalButtons}
      CustomTableRow={customRow ?? TasksListTableRow}
      customTableRowAction={onCustomAction}
      expandableRows
      filters={filters}
      getTableDataRequest={req}
      idKey="taskId"
      init={init}
      initParams={{
        ...initialTasksListTableState,
        params: {
          ...initialTasksListSearchFormModel,
          assignedUserId: userSelectOption,
          taskStatuses: initialTaskStatuses,
          ...initialParams,
        },
      }}
      isLoading={isLoading}
      quickFilters={quickFilters}
      size={size}
      tableHead={tableHead}
      tableKey={tableKey}
      validation={validation}
    />
  );
};
